/**
 * R-82 — bug report client service with offline queue + flush on reconnect.
 */
import type {
  BugReport,
  BugReportListFilters,
  BugReportStatus,
  BugReportSubmitInput,
} from '../../types/bugReport';
import {
  auditSeverityToLegacy,
  legacySeverityToAudit,
} from '../../types/bugReport';
import * as bugReportClient from '../../services/bugReportClient';
import {
  drainOfflineBugReportQueue,
  enqueueOfflineBugReport,
} from './offlineQueue';
import { APP_VERSION, BUILD_SHA } from '../appVersion';
import { auth } from '../../firebase';

let flushListenerInstalled = false;

function installFlushOnOnline(): void {
  if (flushListenerInstalled || typeof window === 'undefined') return;
  if (typeof window.addEventListener !== 'function') return;
  flushListenerInstalled = true;
  window.addEventListener('online', () => {
    void flushOfflineBugReports();
  });
}

export function collectBugReportContext(
  context: BugReportSubmitInput['context'],
): BugReportSubmitInput['context'] {
  return {
    ...context,
    url:
      context.url ??
      (typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : undefined),
    browser: context.browser ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
  };
}

function getLastConsoleErrors(): string[] | undefined {
  if (typeof window === 'undefined') return undefined;
  const win = window as unknown as { lastConsoleErrors?: string[] };
  return win.lastConsoleErrors?.slice(-20);
}

function isBrowserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export function buildBugReportSubmitPayload(
  partial: Omit<BugReportSubmitInput, 'context'> & { context: BugReportSubmitInput['context'] },
): BugReportSubmitInput {
  const auditSeverity = partial.auditSeverity ?? 'warn';
  const severity = partial.severity ?? auditSeverityToLegacy(auditSeverity);
  return {
    ...partial,
    severity,
    auditSeverity,
    context: collectBugReportContext(partial.context),
    consoleErrors: partial.consoleErrors ?? getLastConsoleErrors(),
    // Stamp BOTH the release version and the exact build SHA so triage can pin
    // unambiguously which release/build a bug originated from (src/lib/appVersion
    // is the single source of truth — same values shown in login + app chrome).
    appVersion: partial.appVersion ?? APP_VERSION,
    buildSha: partial.buildSha ?? BUILD_SHA,
    userEmail: partial.userEmail ?? auth.currentUser?.email ?? undefined,
  };
}

export async function submitBugReport(report: BugReportSubmitInput): Promise<string> {
  installFlushOnOnline();
  const payload = buildBugReportSubmitPayload(report);
  if (!isBrowserOnline()) {
    return enqueueOfflineBugReport(payload);
  }
  try {
    return await bugReportClient.submitBugReport(payload);
  } catch (err) {
    if (!isBrowserOnline()) {
      return enqueueOfflineBugReport(payload);
    }
    if (err instanceof Error) throw err;
    throw new Error(typeof err === 'string' ? err : 'Bug report submit failed');
  }
}

export async function flushOfflineBugReports(): Promise<void> {
  if (!isBrowserOnline()) return;
  const queued = await drainOfflineBugReportQueue();
  for (const item of queued) {
    await bugReportClient.submitBugReport(item.payload);
  }
}

export async function listBugReports(filters: BugReportListFilters = {}): Promise<BugReport[]> {
  const all = await bugReportClient.fetchBugQueue();
  return all
    .map(normalizeBugReport)
    .filter((r) => matchesFilters(r, filters))
    .sort((a, b) => {
      const at = a.createdAt ?? a.submittedAt;
      const bt = b.createdAt ?? b.submittedAt;
      return bt.localeCompare(at);
    });
}

export async function updateBugReportStatus(
  id: string,
  status: BugReportStatus,
  triageNote?: string,
  duplicateOfId?: string,
  resolutionNote?: string,
): Promise<void> {
  await bugReportClient.updateBugStatus(id, status, { triageNote, duplicateOfId, resolutionNote });
}

function normalizeBugReport(r: BugReport): BugReport {
  const createdAt = r.createdAt ?? r.submittedAt;
  const userEmail = r.userEmail ?? r.submittedBy.email;
  const auditSeverity = r.auditSeverity ?? legacySeverityToAudit(r.severity);
  return { ...r, createdAt, userEmail, auditSeverity };
}

function matchesFilters(r: BugReport, filters: BugReportListFilters): boolean {
  if (filters.severity && r.auditSeverity !== filters.severity) return false;
  if (filters.category && r.category !== filters.category) return false;
  if (filters.status && r.status !== filters.status) return false;
  if (filters.siteId && r.context.siteId !== filters.siteId) return false;
  const ts = r.createdAt ?? r.submittedAt;
  if (filters.dateFrom && ts < filters.dateFrom) return false;
  if (filters.dateTo && ts > filters.dateTo) return false;
  return true;
}
